// src/services/promotionService.js
import { supabase } from './supabase';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://kcwaqseendhxbsnpmlkg.supabase.co';
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const STORAGE_BUCKET = 'property-images'; // Reusing established storage bucket with public read access
const FALLBACK_STORAGE_KEY = 'nobrokerage_promotion_videos_fallback';

// Default mock promotion videos for preview/fallback
const INITIAL_FALLBACK_PROMOTIONS = [
  {
    id: 'f001-brand-promo',
    title: 'Zero Brokerage Advantage: Buy, Sell & Rent Direct',
    description: 'Watch how NoBrokerage eliminates middlemen and saves lakhs in brokerage fees across Ghazipur, Varanasi & Lucknow.',
    source_type: 'youtube',
    video_url: 'https://www.youtube.com/watch?v=ScMzIvxBSi4',
    thumbnail_url: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1200&q=80',
    show_on: 'Both',
    position: 'Promotion Section',
    priority: 1,
    status: 'Active',
    start_date: null,
    end_date: null,
    created_at: new Date().toISOString()
  },
  {
    id: 'f002-brand-promo',
    title: 'Premium Construction & Turnkey Building Services',
    description: 'From 3D Elevation & Soil Testing to Interior Finishing with 100% Guaranteed Timely Handover.',
    source_type: 'youtube',
    video_url: 'https://www.youtube.com/watch?v=ScMzIvxBSi4',
    thumbnail_url: 'https://images.unsplash.com/photo-1541888946425-d0fbb186c5f7?auto=format&fit=crop&w=1200&q=80',
    show_on: 'Both',
    position: 'Homepage Hero',
    priority: 2,
    status: 'Active',
    start_date: null,
    end_date: null,
    created_at: new Date().toISOString()
  }
];

const getLocalFallback = () => {
  try {
    const raw = localStorage.getItem(FALLBACK_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    console.error('Error reading promotion fallback storage:', e);
  }
  return INITIAL_FALLBACK_PROMOTIONS;
};

const setLocalFallback = (list) => {
  try {
    localStorage.setItem(FALLBACK_STORAGE_KEY, JSON.stringify(list));
  } catch (e) {
    console.error('Error saving promotion fallback storage:', e);
  }
};

/**
 * Extracts YouTube Video ID from any standard or shortened YouTube link.
 */
export const extractYouTubeId = (url) => {
  if (!url) return null;
  const regExp = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i;
  const match = url.match(regExp);
  return match ? match[1] : null;
};

/**
 * Returns High Quality Thumbnail URL for a YouTube Video ID.
 */
export const getYouTubeThumbnail = (videoId) => {
  if (!videoId) return null;
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
};

/**
 * Fetches thumbnail from Vimeo via oEmbed API.
 */
export const fetchVimeoThumbnail = async (vimeoUrl) => {
  try {
    const res = await fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(vimeoUrl)}`);
    if (res.ok) {
      const data = await res.json();
      return data.thumbnail_url || null;
    }
  } catch (e) {
    console.warn('Vimeo oEmbed fetch failed:', e);
  }
  return null;
};

/**
 * Uploads media file to Supabase Storage with XMLHttpRequest for live progress updates.
 * Performs client-side MIME check, sanitization, and random filename generation.
 */
export const uploadMediaFile = (file, prefix = 'promo', onProgress = null) => {
  return new Promise((resolve, reject) => {
    // 1. MIME Validation
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');

    if (isVideo && !['video/mp4', 'video/webm'].includes(file.type)) {
      return reject(new Error('Only MP4 and WebM video formats are allowed.'));
    }
    if (isImage && !['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      return reject(new Error('Only JPG, PNG, and WebP thumbnail formats are allowed.'));
    }

    // 2. Max size validation (60MB for video, 5MB for thumbnail)
    const maxMb = isVideo ? 60 : 5;
    if (file.size > maxMb * 1024 * 1024) {
      return reject(new Error(`File size exceeds maximum allowed limit of ${maxMb} MB.`));
    }

    // 3. Sanitized randomized filename
    const ext = file.name.split('.').pop().toLowerCase().replace(/[^a-z0-9]/g, '') || (isVideo ? 'mp4' : 'jpg');
    const randomHex = Math.random().toString(36).substring(2, 9);
    const fileName = `${prefix}_${Date.now()}_${randomHex}.${ext}`;
    const uploadUrl = `${SUPABASE_URL}/storage/v1/object/${STORAGE_BUCKET}/${fileName}`;

    // 4. Perform upload with XMLHttpRequest for live progress bar
    const xhr = new XMLHttpRequest();
    xhr.open('POST', uploadUrl);
    xhr.setRequestHeader('apikey', SUPABASE_KEY);
    xhr.setRequestHeader('Authorization', `Bearer ${SUPABASE_KEY}`);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.setRequestHeader('x-upsert', 'true');

    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const publicUrl = `${SUPABASE_URL}/storage/v1/object/public/${STORAGE_BUCKET}/${fileName}`;
        resolve(publicUrl);
      } else {
        reject(new Error(`Upload failed with server status ${xhr.status}: ${xhr.statusText}`));
      }
    };

    xhr.onerror = () => reject(new Error('Network error during file upload. Please check connection.'));
    xhr.send(file);
  });
};

export const promotionService = {
  /**
   * Fetch all promotion videos with optional sorting and filters
   */
  async getPromotions() {
    try {
      const { data, error } = await supabase
        .from('promotion_videos')
        .select('*')
        .order('priority', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) {
        console.warn('Supabase promotion_videos table not available or error:', error.message);
        return { data: getLocalFallback(), isFallback: true };
      }

      return { data: data || [], isFallback: false };
    } catch (err) {
      console.warn('Fallback to local promotions due to error:', err);
      return { data: getLocalFallback(), isFallback: true };
    }
  },

  /**
   * Create a new promotion video
   */
  async createPromotion(promoData) {
    try {
      const payload = {
        title: promoData.title.trim(),
        description: promoData.description ? promoData.description.trim() : null,
        source_type: promoData.source_type || 'youtube',
        video_url: promoData.video_url.trim(),
        thumbnail_url: promoData.thumbnail_url ? promoData.thumbnail_url.trim() : null,
        show_on: promoData.show_on || 'Both',
        position: promoData.position || 'Promotion Section',
        priority: parseInt(promoData.priority, 10) || 1,
        status: promoData.status || 'Active',
        start_date: promoData.start_date || null,
        end_date: promoData.end_date || null,
        created_by: promoData.created_by || null,
      };

      const { data, error } = await supabase
        .from('promotion_videos')
        .insert([payload])
        .select()
        .single();

      if (error) {
        console.warn('Inserting into fallback storage due to error:', error.message);
        const fallbackList = getLocalFallback();
        const newRecord = {
          ...payload,
          id: `local-${Date.now()}`,
          created_at: new Date().toISOString()
        };
        fallbackList.push(newRecord);
        setLocalFallback(fallbackList);
        return { data: newRecord, error: null, isFallback: true };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Failed to create promotion:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Update an existing promotion video
   */
  async updatePromotion(id, promoData) {
    try {
      const payload = {
        title: promoData.title.trim(),
        description: promoData.description ? promoData.description.trim() : null,
        source_type: promoData.source_type,
        video_url: promoData.video_url.trim(),
        thumbnail_url: promoData.thumbnail_url ? promoData.thumbnail_url.trim() : null,
        show_on: promoData.show_on,
        position: promoData.position,
        priority: parseInt(promoData.priority, 10) || 1,
        status: promoData.status,
        start_date: promoData.start_date || null,
        end_date: promoData.end_date || null,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('promotion_videos')
        .update(payload)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        const fallbackList = getLocalFallback().map(item =>
          item.id === id ? { ...item, ...payload } : item
        );
        setLocalFallback(fallbackList);
        return { data: payload, error: null, isFallback: true };
      }

      return { data, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  },

  /**
   * Delete a promotion video
   */
  async deletePromotion(id, videoUrl = null) {
    try {
      const { error } = await supabase
        .from('promotion_videos')
        .delete()
        .eq('id', id);

      if (error) {
        const fallbackList = getLocalFallback().filter(item => item.id !== id);
        setLocalFallback(fallbackList);
      }

      // Cleanup uploaded file from storage if applicable
      if (videoUrl && videoUrl.includes(STORAGE_BUCKET)) {
        try {
          const parts = videoUrl.split(`/${STORAGE_BUCKET}/`);
          if (parts[1]) {
            await supabase.storage.from(STORAGE_BUCKET).remove([parts[1]]);
          }
        } catch (storageErr) {
          console.warn('Storage cleanup notice:', storageErr);
        }
      }

      return { success: true };
    } catch (err) {
      return { success: false, error: err };
    }
  },

  /**
   * Toggle promotion video status (Active <-> Inactive)
   */
  async toggleStatus(id, currentStatus) {
    const nextStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';
    return this.updatePromotion(id, { status: nextStatus });
  },

  /**
   * Update reordered priorities
   */
  async updatePriorities(orderedPromotions) {
    try {
      for (let i = 0; i < orderedPromotions.length; i++) {
        const item = orderedPromotions[i];
        await supabase
          .from('promotion_videos')
          .update({ priority: i + 1 })
          .eq('id', item.id);
      }
      return { success: true };
    } catch (e) {
      console.warn('Priority update warning:', e);
      return { success: false, error: e };
    }
  }
};

